'use client'

/**
 * Tables for /data/price-by-size — brand-standard treatment matching the
 * price-index, energy-index, and brand-index tables (white card, gray-50
 * uppercase header, hairline dividers, right-aligned tabular numerals,
 * overflow-x scroll container), click-to-sort via the shared SortableTable.
 *
 * Two tables:
 *  • SizeClassTable — the dollar-price cross-section by cubic-foot band.
 *  • TypePriceTable — median advertised price by appliance type (published
 *    types only; below-threshold types are disclosed separately on the page).
 *
 * Every value arrives as a plain row from app/data/price-by-size/page.tsx, which
 * owns the single importer of app/data/_data/price-by-size.json — sorting only
 * reorders, it never derives. A null figure renders as an em dash: absent,
 * never estimated.
 */
import { SortableTable, type SortableColumn } from '../_components/sortable-table'
import { EntityLink, type LinkTarget } from '../_components/entity-link'
import type { SizeBucketRow, TypeRow } from './price-by-size-summary'
import { formatUsd, formatCuft } from './price-by-size-summary'

/** Rows plus the live page the server resolved for the row's entity (FRIDGE-334). */
export type LinkedSizeBucketRow = SizeBucketRow & { link?: LinkTarget }
export type LinkedTypeRow = TypeRow & { link?: LinkTarget }

function fmtInt(n: number): string {
  return n.toLocaleString('en-US')
}

export function SizeClassTable({ rows }: { rows: LinkedSizeBucketRow[] }) {
  const columns: SortableColumn<LinkedSizeBucketRow>[] = [
    {
      key: 'label',
      header: 'Size class',
      sortValue: (r) => r.median_price,
      render: (r) => (
        <EntityLink link={r.link} className="font-semibold">
          {r.label}
        </EntityLink>
      ),
    },
    {
      key: 'n',
      header: 'Products',
      numeric: true,
      sortValue: (r) => r.n,
      render: (r) => <span className="font-bold">{fmtInt(r.n)}</span>,
    },
    {
      key: 'p10_price',
      header: 'P10 price',
      numeric: true,
      sortValue: (r) => r.p10_price,
      render: (r) => <span className="text-gray-500">{formatUsd(r.p10_price)}</span>,
    },
    {
      key: 'median_price',
      header: 'Median price',
      numeric: true,
      sortValue: (r) => r.median_price,
      render: (r) => <span className="font-semibold">{formatUsd(r.median_price)}</span>,
    },
    {
      key: 'p90_price',
      header: 'P90 price',
      numeric: true,
      sortValue: (r) => r.p90_price,
      render: (r) => <span className="text-gray-500">{formatUsd(r.p90_price)}</span>,
    },
    {
      key: 'min_price',
      header: 'Lowest',
      numeric: true,
      sortValue: (r) => r.min_price,
      render: (r) => <span className="text-gray-500">{formatUsd(r.min_price)}</span>,
    },
    {
      key: 'max_price',
      header: 'Highest',
      numeric: true,
      sortValue: (r) => r.max_price,
      render: (r) => <span className="text-gray-500">{formatUsd(r.max_price)}</span>,
    },
  ]
  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.bucket}
      defaultSortKey="label"
      defaultDir="asc"
      minWidth={720}
      caption="Refrigerator Price by Size — median advertised price and 10th-to-90th-percentile range by cubic-foot class, sortable | Fridge.com"
    />
  )
}

export function TypePriceTable({ rows }: { rows: LinkedTypeRow[] }) {
  const columns: SortableColumn<LinkedTypeRow>[] = [
    {
      key: 'label',
      header: 'Type',
      sortValue: (r) => r.label,
      render: (r) => (
        <EntityLink link={r.link} className="font-semibold">
          {r.label}
        </EntityLink>
      ),
    },
    {
      key: 'n',
      header: 'Products',
      numeric: true,
      sortValue: (r) => r.n,
      render: (r) => <span className="font-bold">{fmtInt(r.n)}</span>,
    },
    {
      key: 'median_cuft',
      header: 'Median ft³',
      numeric: true,
      sortValue: (r) => r.median_cuft ?? -1,
      render: (r) => (r.median_cuft === null ? '—' : formatCuft(r.median_cuft)),
    },
    {
      key: 'p10_price',
      header: 'P10 price',
      numeric: true,
      sortValue: (r) => r.p10_price ?? -1,
      render: (r) => <span className="text-gray-500">{formatUsd(r.p10_price)}</span>,
    },
    {
      key: 'median_price',
      header: 'Median price',
      numeric: true,
      sortValue: (r) => r.median_price ?? -1,
      render: (r) => <span className="font-semibold">{formatUsd(r.median_price)}</span>,
    },
    {
      key: 'p90_price',
      header: 'P90 price',
      numeric: true,
      sortValue: (r) => r.p90_price ?? -1,
      render: (r) => <span className="text-gray-500">{formatUsd(r.p90_price)}</span>,
    },
  ]
  return (
    <SortableTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.type}
      defaultSortKey="median_price"
      defaultDir="desc"
      minWidth={720}
      caption="Refrigerator median advertised price by appliance type, sortable | Fridge.com"
    />
  )
}
